import { Link, useParams } from "react-router-dom";
import { useState } from "react";
import { Phone, ArrowLeft, ArrowRight, CheckCircle2, Truck, Wind, ShieldAlert, Layers, Cog, PackageSearch, Snowflake } from "lucide-react";
import { MarketingNav } from "./MarketingNav";
import { MarketingFooter } from "./MarketingFooter";
import { Seo } from "./Seo";
import { Reveal } from "./anim";
import { FLEET, FLEET_GALLERY, FLEET_FAQ, IMAGES, CONTACT, SINCE } from "./marketingData";

const ICONS = { Wind, ShieldAlert, Layers, Cog, Truck, PackageSearch, Snowflake };

export default function FleetPage() {
  const { slug } = useParams();
  const [active, setActive] = useState(0);
  const item = FLEET.find((f) => f.slug === slug);

  if (!item) {
    return (
      <div className="bg-[#F9F8F6] text-[#1A1A18] min-h-screen">
        <MarketingNav />
        <div className="max-w-3xl mx-auto px-5 py-40 text-center">
          <h1 className="font-heading font-bold text-3xl mb-4">Vehicle not found</h1>
          <Link to="/fleet" className="text-[#C84B31] font-semibold inline-flex items-center gap-1.5"><ArrowLeft className="w-4 h-4" /> Back to our fleet</Link>
        </div>
        <MarketingFooter />
      </div>
    );
  }

  const Icon = ICONS[item.icon] || Truck;
  const gallery = (FLEET_GALLERY[item.slug] && FLEET_GALLERY[item.slug].length) ? FLEET_GALLERY[item.slug] : [IMAGES[item.img] || IMAGES.fleetYard];
  const hero = gallery[Math.min(active, gallery.length - 1)];
  const others = FLEET.filter((f) => f.slug !== slug).slice(0, 3);
  const jsonLd = [
    {
      "@context": "https://schema.org", "@type": "Product",
      name: item.name, description: item.desc, image: gallery,
      category: "Freight transport vehicle",
      brand: { "@type": "Organization", name: "Indian Transport Organisation" },
      offers: { "@type": "Offer", availability: "https://schema.org/InStock", priceCurrency: "INR", price: "0", url: `https://itoworldwide.com/fleet/${item.slug}` },
    },
    {
      "@context": "https://schema.org", "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://itoworldwide.com/" },
        { "@type": "ListItem", position: 2, name: "Fleet", item: "https://itoworldwide.com/fleet" },
        { "@type": "ListItem", position: 3, name: item.name, item: `https://itoworldwide.com/fleet/${item.slug}` },
      ],
    },
  ];

  const bestForTxt = (item.bestFor || []).join(", ");
  const long2 = `Operating pan-India since ${SINCE}, Indian Transport Organisation moves ${item.name.toLowerCase()} to any pincode by road — with route surveys, statutory permits and pilot escorts arranged end to end on a single Lorry Receipt. Backed by 800+ owned vehicles, live GPS tracking and a dedicated coordinator, your cargo is planned, permitted and delivered without surprises.`;
  const specs = [
    { label: "Coverage", value: "Pan-India — delivery to any pincode" },
    { label: "Best suited for", value: bestForTxt || "Industrial & project cargo" },
    { label: "Permits & escorts", value: "Statutory permits, RTO clearances & pilot vehicles arranged by ITO" },
    { label: "Tracking", value: "Live GPS with a dedicated movement coordinator" },
  ];
  const genericFaqs = [
    { q: `What can ${item.name.toLowerCase()} carry?`, a: `They are ideal for ${(bestForTxt || "industrial and project cargo").toLowerCase()}. Share your cargo dimensions and weight and we'll confirm the right configuration.` },
    { q: `Which routes do you cover?`, a: `We operate across India to any pincode, including over-dimensional route surveys and multi-state permits for ODC movements.` },
    { q: `Do you arrange permits and escorts?`, a: `Yes — statutory permits, RTO clearances and pilot/escort vehicles are handled by ITO as part of the movement.` },
  ];
  const faqs = FLEET_FAQ[item.slug] || genericFaqs;
  jsonLd.push({
    "@context": "https://schema.org", "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
  });

  return (
    <div className="bg-[#F9F8F6] text-[#1A1A18] font-body antialiased overflow-x-hidden" data-testid={`fleet-page-${item.slug}`}>
      <Seo
        title={`${item.name} in India — Transporter & Hire | ITO (since ${SINCE})`}
        description={item.desc.slice(0, 180)}
        path={`/fleet/${item.slug}`}
        image={gallery[0]}
        imageAlt={`${item.name} operated by Indian Transport Organisation on an Indian highway`}
        keywords={`${item.name.toLowerCase()} india, ${item.name.toLowerCase()} transporter india, ${item.name.toLowerCase()} hire, ${(item.bestFor || []).map((b) => b.toLowerCase()).join(", ")}`}
        type="product"
        jsonLd={jsonLd}
      />
      <MarketingNav heroDark />

      {/* Hero */}
      <section className="relative min-h-[62vh] flex items-end pb-14 pt-32 overflow-hidden">
        <img src={hero} alt={item.name} className="absolute inset-0 w-full h-full object-cover transition-opacity duration-300" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A18]/92 via-[#1A1A18]/55 to-[#1A1A18]/25" />
        <div className="relative z-10 max-w-5xl mx-auto px-5 sm:px-8 w-full">
          <Link to="/fleet" data-testid="fleet-back" className="inline-flex items-center gap-1.5 text-white/85 hover:text-white text-sm font-semibold mb-5"><ArrowLeft className="w-4 h-4" /> Our fleet</Link>
          <span className="inline-flex w-12 h-12 rounded-xl items-center justify-center bg-[#C84B31] mb-5"><Icon className="w-6 h-6 text-white" /></span>
          <h1 className="font-heading font-black tracking-tight text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.03] max-w-3xl">{item.name}</h1>
          {gallery.length > 1 && (
            <div className="flex gap-3 mt-6" data-testid="fleet-thumbs">
              {gallery.map((g, i) => (
                <button key={g} onClick={() => setActive(i)} data-testid={`fleet-thumb-${i}`}
                  className={`h-16 w-24 rounded-lg overflow-hidden border-2 transition-all ${i === active ? "border-[#C84B31]" : "border-white/30 hover:border-white/70"}`}>
                  <img src={g} alt={`${item.name} ${i + 1}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Body */}
      <section className="py-20">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 grid lg:grid-cols-3 gap-12">
          <Reveal className="lg:col-span-2">
            <p className="text-xs font-semibold tracking-widest uppercase text-[#C84B31] mb-3">Overview</p>
            <p className="text-lg text-[#4F4C44] leading-relaxed">{item.desc}</p>
            <p className="text-base text-[#4F4C44] leading-relaxed mt-4" data-testid="fleet-long-desc">{long2}</p>

            <div className="mt-10" data-testid="fleet-specs">
              <h2 className="font-heading font-bold text-2xl mb-5">Key details</h2>
              <dl className="divide-y divide-[#1A1A18]/10 border-y border-[#1A1A18]/10">
                {specs.map((s) => (
                  <div key={s.label} className="flex flex-col sm:flex-row sm:gap-6 py-3">
                    <dt className="text-sm font-semibold text-[#1A1A18] sm:w-44 shrink-0">{s.label}</dt>
                    <dd className="text-sm text-[#4F4C44]">{s.value}</dd>
                  </div>
                ))}
              </dl>
            </div>

            {item.bestFor?.length > 0 && (
              <div className="mt-10">
                <h2 className="font-heading font-bold text-2xl mb-5">Best for</h2>
                <ul className="grid sm:grid-cols-2 gap-3">
                  {item.bestFor.map((b) => (
                    <li key={b} className="flex items-center gap-3 text-[#1A1A18]">
                      <CheckCircle2 className="w-5 h-5 text-[#C84B31] shrink-0" /> {b}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="mt-10" data-testid="fleet-faq">
              <h2 className="font-heading font-bold text-2xl mb-5">Frequently asked</h2>
              <div className="space-y-4">
                {faqs.map((f) => (
                  <div key={f.q} className="border border-[#1A1A18]/10 rounded-xl p-5">
                    <h3 className="font-semibold text-[#1A1A18] mb-1.5">{f.q}</h3>
                    <p className="text-sm text-[#4F4C44] leading-relaxed">{f.a}</p>
                  </div>
                ))}
              </div>
            </div>

            {(() => {
              const RES = {
                "air-suspension-trailer": { guide: "air-suspension-trailer-transport-india", city: "/air-suspension-trailer-transporter" },
                "low-bed-trailer": { guide: "low-bed-trailer-transport-india", city: "/low-bed-trailer-transporter" },
                "semi-low-bed-trailer": { guide: "low-bed-trailer-transport-india", city: "/low-bed-trailer-transporter" },
                "modular-heavy-haul-trailer": { guide: "odc-heavy-haul-transport-india", city: "/odc-heavy-haul-transporter" },
              };
              const r = RES[item.slug];
              if (!r) return null;
              return (
                <div className="mt-10 rounded-2xl border border-[#C84B31]/25 bg-[#C84B31]/[0.04] p-6" data-testid="fleet-resources">
                  <h2 className="font-heading font-bold text-xl mb-3">Guides &amp; city coverage</h2>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Link to={`/guides/${r.guide}`} data-testid="fleet-guide-link"
                      className="inline-flex items-center gap-2 text-sm font-semibold text-[#C84B31] hover:underline">
                      Read the buyer's guide <ArrowRight className="w-4 h-4" />
                    </Link>
                    <Link to={r.city} data-testid="fleet-city-link"
                      className="inline-flex items-center gap-2 text-sm font-semibold text-[#C84B31] hover:underline">
                      Transporter by city <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              );
            })()}
          </Reveal>

          {/* CTA card */}
          <Reveal delay={0.1} className="lg:col-span-1">
            <div className="rounded-2xl bg-[#1A1A18] text-white p-8 sticky top-24" data-testid="fleet-cta-card">
              <h3 className="font-heading font-bold text-xl mb-2">Need this vehicle?</h3>
              <p className="text-white/75 text-sm leading-relaxed mb-6">Tell us your cargo, route and dates — we'll quote your {item.name.toLowerCase()} movement to any pincode in India.</p>
              <a href={CONTACT.phoneHref} data-testid="fleet-quote-btn" className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#C84B31] px-6 py-3 text-sm font-semibold text-white hover:bg-[#a83c26] transition-colors">
                <Phone className="w-4 h-4" /> Get a quote
              </a>
              <a href={CONTACT.emailHref} className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-full border border-white/25 px-6 py-3 text-sm font-semibold text-white hover:bg-white hover:text-[#1A1A18] transition-colors">
                Email us
              </a>
              <div className="mt-6 text-white/70 text-sm">
                <div className="font-semibold text-white">{CONTACT.phone}</div>
                <div className="break-all">{CONTACT.email}</div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Other vehicles */}
      <section className="py-16 bg-white border-t border-[#1A1A18]/10">
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <h2 className="font-heading font-bold text-2xl mb-8">Other vehicles in our fleet</h2>
          <div className="grid sm:grid-cols-3 gap-6">
            {others.map((o) => {
              const OIcon = ICONS[o.icon] || Truck;
              return (
                <Link key={o.slug} to={`/fleet/${o.slug}`} data-testid={`fleet-other-${o.slug}`}
                  className="group block rounded-2xl border border-[#1A1A18]/10 p-6 hover:border-[#C84B31]/50 hover:shadow-lg transition-all">
                  <span className="inline-flex w-10 h-10 rounded-xl items-center justify-center bg-[#C84B31]/10 mb-4"><OIcon className="w-5 h-5 text-[#C84B31]" /></span>
                  <h3 className="font-heading font-semibold text-lg mb-2">{o.name}</h3>
                  <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#C84B31]">View <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" /></span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
